// Accepted file types for the Agent Knowledge Base document upload (Train
// step). Shared between UploadScreen.tsx (initial drop zone) and
// KBEditor.tsx (in-editor "Upload files" button) so the two entry points
// never drift out of sync.
export const ACCEPTED_KB_MIME_TYPES = [
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
];

export const ACCEPTED_KB_FILE_EXTENSIONS =
  '.pdf,.ppt,.pptx,.doc,.docx,.txt,.csv';

// Fallback matcher for files whose browser-reported MIME type is empty
// (common for some OS/file-manager combinations, notably .csv on macOS).
export const ACCEPTED_KB_FILE_EXTENSIONS_REGEX =
  /\.(pdf|ppt|pptx|doc|docx|txt|csv)$/i;

// KB topic names — matches sectionManifests in FAI rx-agent.constants.ts
export const KB_TOPICS = [
  'Brand Overview',
  'Positions',
  'Compensation',
  'Application Process & Timeline',
  'Working Hours',
  'Background Check',
  'Equipment Requirements',
  'Documents/ Certification/Licenses/ Physicals',
  'I-9 Verification',
  'Payments from Applicants',
  'W-9 Tax Form',
  'Technical Issues',
];

// Placeholder text per topic — sourced from sectionManifests information arrays
export const TOPIC_PLACEHOLDERS: Record<string, string> = {
  'Brand Overview':
    "Provide a brief overview of the company, including its mission, industry, and key achievements. Describe the working atmosphere and the company's employee care initiatives.",
  Positions:
    'List all positions to be filled. For each position include: brief job description, hiring type (independent contractor or company payroll), approx. pay range, typical work shifts, job location (remote or on-site), and physical/education requirements. If there are many positions, provide a high-level overview of the different types.',
  Compensation:
    'Include: benefits and perks offered, whether pay is negotiable, approx. pay range for each position, and any opportunities for tips or commissions on top of the pay range.',
  'Application Process & Timeline':
    'Outline the steps from initial application to final decision. Specify any timelines if applicable, including timelines for specific stages.',
  'Working Hours':
    'Indicate whether the applicant can set their own hours or if shifts are predetermined, and list common work hours and shifts.',
  'Background Check':
    'Include: how long the background check takes, the process (including fingerprinting if applicable), where applicants can check their status, whether applicants pay for it, and whether the company hires convicted felons.',
  'Equipment Requirements':
    'List all equipment needed by the applicant. If a smartphone is required, specify the minimum iOS and Android versions. If it is a delivery driver role, specify vehicle requirements.',
  'Documents/ Certification/Licenses/ Physicals':
    'Include: what documents, certifications, or licenses are required; how applicants can upload them; who to contact for help; and how long document verification takes.',
  'I-9 Verification':
    'Describe the employment verification process and how applicants should reach out for help if they are unable to complete it.',
  'Payments from Applicants':
    'List what applicants need to pay for (e.g. background check, delivery bags) and whether payment is required before or after the offer letter is issued.',
  'W-9 Tax Form':
    'Explain why the applicant needs to provide tax information, what information they need to provide and how, and how to reach out for help if they run into any problems.',
  'Technical Issues':
    'Provide steps to resolve common issues such as: unable to login, unable to download the app, expired activation or verification links, issues receiving passcode or login credentials. Include how applicants can escalate if the issue persists.',
};

// Example candidate questions per topic — shown as tooltip in the editor
export const TOPIC_EXAMPLE_QUESTIONS: Record<string, string[]> = {
  'Brand Overview': [
    'What does your company do?',
    'What is the company culture like?',
    'How does the company support its employees?',
  ],
  Positions: [
    'What positions are available?',
    'Is this a full-time or part-time role?',
    'What are the job requirements?',
    'Is the position remote or on-site?',
  ],
  Compensation: [
    'What is the pay range for this role?',
    'Are there any benefits or perks?',
    'Is the pay negotiable?',
    'Are there opportunities for tips or commissions?',
  ],
  'Application Process & Timeline': [
    'What are the steps in the application process?',
    'How long does it take to hear back after applying?',
    'What happens after I submit my application?',
  ],
  'Working Hours': [
    'Can I set my own hours?',
    'What are the typical work shifts?',
    'Are weekends required?',
  ],
  'Background Check': [
    'How long does the background check take?',
    'What is the background check process?',
    'Where can I check the status of my background check?',
    'Will I have to pay for the background check?',
    'Does the company hire convicted felons?',
  ],
  'Equipment Requirements': [
    'What equipment do I need for this job?',
    'Do I need a smartphone? What version?',
    'Do I need my own vehicle?',
  ],
  'Documents/ Certification/Licenses/ Physicals': [
    'What documents do I need to provide?',
    'How do I upload my documents?',
    'Who do I contact if I have trouble uploading?',
    'How long does document verification take?',
  ],
  'I-9 Verification': [
    'What is the I-9 verification process?',
    'What documents do I need for I-9?',
    'Who can I contact if I need help with verification?',
  ],
  'Payments from Applicants': [
    'Do I need to pay for anything during the application?',
    'When do I need to make the payment?',
    'What is the payment for?',
  ],
  'W-9 Tax Form': [
    'Why do I need to provide tax information?',
    'What information do I need to submit?',
    'How do I submit my tax information?',
    'Who do I contact if I have issues?',
  ],
  'Technical Issues': [
    'I cannot log in to the app — what should I do?',
    'How do I download the app?',
    'My activation link has expired — what now?',
    'I am not receiving my login credentials — how do I get help?',
  ],
};
